/**
 * HouseholdRiskIndicatorsList — aggregates every Risk Indicator across the
 * household into a single alert-style list. Reuses the visual treatment of
 * the Applicant Profile's RiskIndicatorsSection so both pages feel like one
 * product.
 *
 * Each card shows the indicator + a small "Member · {name}" eyebrow so the
 * PM can immediately see which applicant the observation lives on, and
 * jumps into that applicant's profile with one click.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, ShieldCheck, ExternalLink, Users } from "lucide-react";

const STATUS_TONE = {
  open:          { label: "Open",         cls: "bg-amber-50 text-amber-700 border-amber-200",       accent: "border-l-amber-500" },
  reviewed:      { label: "Reviewed",     cls: "bg-blue-50 text-blue-700 border-blue-200",          accent: "border-l-blue-500" },
  under_review:  { label: "Under review", cls: "bg-blue-50 text-blue-700 border-blue-200",          accent: "border-l-blue-500" },
  resolved:      { label: "Resolved",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200", accent: "border-l-emerald-500" },
  dismissed:     { label: "Dismissed",    cls: "bg-slate-100 text-slate-600 border-slate-200",      accent: "border-l-slate-400" },
};

const ACTIVE_STATUSES = new Set(["open", "under_review", "reviewed"]);

export default function HouseholdRiskIndicatorsList({ group, onCountChange, onIndicatorsChange, testid = "household-risk-indicators" }) {
  const members = group.member_screenings || [];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(
          members.map((m) =>
            apiClient.get(`/screenings/${m.screening_id}/risk-indicators`)
              .then((r) => (r.data || []).map((ind) => ({
                ...ind,
                _member_name: (m.applicant_name && m.applicant_name.trim()) || "Member",
                _member_id: m.screening_id,
              })))
              .catch(() => [])
          )
        );
        if (cancelled) return;
        const flat = results.flat();
        // Sort: active (open/under_review/reviewed) first, then by created_at desc.
        flat.sort((a, b) => {
          const ac = ACTIVE_STATUSES.has(a.status) ? 0 : 1;
          const bc = ACTIVE_STATUSES.has(b.status) ? 0 : 1;
          if (ac !== bc) return ac - bc;
          return (b.created_at || "").localeCompare(a.created_at || "");
        });
        setItems(flat);
        if (onCountChange) onCountChange(flat.filter((i) => ACTIVE_STATUSES.has(i.status)).length);
        if (onIndicatorsChange) onIndicatorsChange(flat);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [group.id]);

  const active = items.filter((i) => ACTIVE_STATUSES.has(i.status));

  return (
    <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid={testid}>
      <header className={`flex items-center justify-between gap-3 px-5 py-3.5 border-b ${active.length > 0 ? "bg-amber-50/40 border-amber-100" : "bg-emerald-50/40 border-emerald-100"}`}>
        <div className="flex items-start gap-2.5 min-w-0">
          <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ring-1 ${active.length > 0 ? "bg-amber-50 ring-amber-200" : "bg-emerald-50 ring-emerald-200"}`}>
            {active.length > 0
              ? <ShieldAlert className="h-4 w-4 text-amber-700" />
              : <ShieldCheck className="h-4 w-4 text-emerald-700" />}
          </div>
          <div className="min-w-0">
            <div className="ntn-eyebrow">
              Household Risk Indicators
            </div>
            <div className="ntn-h3 mt-0.5">
              {active.length === 0
                ? "No Risk Indicators Found"
                : active.length === 1
                  ? "Potential Risk Indicators Found"
                  : "Elevated Risk Indicators Found"}
            </div>
            <div className="ntn-caption mt-0.5">
              Indicators raised on any member roll up here. Detail lives on the applicant profile.
            </div>
          </div>
        </div>
        {active.length > 0 && (
          <Badge variant="outline" className="ntn-eyebrow bg-white border-amber-200 text-amber-800" data-testid={`${testid}-count-badge`}>
            {active.length}
          </Badge>
        )}
      </header>

      <div className="p-5">
        {loading ? (
          <div className="ntn-secondary py-4 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center" data-testid={`${testid}-empty`}>
            <div className="inline-flex h-14 w-14 rounded-full bg-emerald-50 ring-2 ring-emerald-100 items-center justify-center mb-3">
              <ShieldCheck className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="ntn-h3">Clean Household</div>
            <div className="ntn-secondary mt-1.5 max-w-sm mx-auto">
              No risk indicators have been identified across any member of this household.
            </div>
          </div>
        ) : (
          <ul className="space-y-4" data-testid={`${testid}-list`}>
            {items.map((ind) => {
              const tone = STATUS_TONE[ind.status] || STATUS_TONE.open;
              return (
                <li
                  key={ind.id}
                  className={`bg-white ring-1 ring-slate-200 rounded-md p-5 border-l-4 ${tone.accent} shadow-sm hover:shadow-md transition-shadow`}
                  data-testid={`household-risk-indicator-${ind.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-bold mb-1.5 inline-flex items-center gap-1">
                        <Users className="h-3 w-3" /> Member · {ind._member_name}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <ShieldAlert className={`h-4 w-4 ${
                          ind.status === "resolved" ? "text-emerald-500"
                          : ind.status === "dismissed" ? "text-slate-400"
                          : ind.status === "open" ? "text-amber-600"
                          : "text-blue-600"
                        }`} />
                        <div className="font-semibold text-slate-900 text-base">{ind.title}</div>
                        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-bold ${tone.cls}`}>{tone.label}</Badge>
                      </div>
                      <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words leading-relaxed">{ind.description}</p>
                    </div>
                    <Link
                      to={`/screenings/${ind._member_id}`}
                      className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 font-semibold shrink-0 px-2.5 py-1 rounded-md hover:bg-slate-50 transition-colors"
                      data-testid={`household-risk-indicator-${ind.id}-open`}
                    >
                      Open applicant <ExternalLink className="h-3 w-3" />
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
